import { useCallback, useMemo } from "react";
import { useNavigation } from "./navigation-context";
import { RecursoSistema } from "@workflow/types";

export type NavigateOptions = {
  openInNewTab?: boolean;
  state?: any;
};

export type LinkRecursoDTO = {
  recurso: RecursoSistema | string;
  params?: Record<string, string | number>;
};

// ---------------------------------------------------------
// 2. ROTAS ESTÁTICAS E DINÂMICAS
// ---------------------------------------------------------
export const ROUTES = {
  home: "/",
  login: "/login",
  purchases: "/compras",
  jobDetails: (id: string | number) => `/job/${id}`,
  jobEdit: (id: string | number) => `/job/${id}/editar`,
  jobPlanejamentoDetails: (id: string | number) => `/jobs/${id}/planejamento`,
  atendimentoDetails: (id: string | number) => `/planilhas/${id}/atendimento`,
  atendimentoEdit: (id: string | number) => `/planilhas/${id}/atendimento/editar`,
  preProducaoDetails: (id: string | number) => `/planilhas/${id}/pre-producao`,
  compraDetails: (id: string | number) => `/planilhas/${id}/compras`,
  preProducaoCreate: (id: string | number) => `/planilhas/${id}/pre-producao/novo`,
  versaoCreate: (id: string | number) => `/planilhas/${id}/pre-producao/versao/nova`,
  atendimentoCreate: (jobId?: string | number) =>
    jobId ? `/planilhas/atendimento/novo?jobId=${jobId}` : `/planilhas/atendimento/novo`,
  compraCreate: (id: string | number) => `/planilhas/${id}/compras/novo`,
  compraEdit: (id: string | number) => `/planilhas/${id}/compras/editar`,
  atendimentoList: "/planilhas/atendimento",
  agendaJobs: "/planilhas/atendimento/agenda",
  preProducaoAgenda: "/planilhas/pre-producao/agenda",
  tecnologiaAgenda: "/jobs/tecnologia/agenda",
  planejamentoAgenda: "/jobs/planejamento/agenda",
  preProducaoList: "/planilhas/pre-producao",
  compraList: "/planilhas/compras",
  loteList: "/lotes",
  faturamentoAtendimentoDetails: (id: string | number) => `/faturamento/atendimento/${id}`,
  faturamentoAtendimentoList: "/faturamento/atendimento",
  faturamentoAtendimentoCreate: "/faturamento/atendimento/novo",
  financeiroFaturamentoList: "/financeiro/faturamentos",
  aprovacaoList: "/aprovacoes",
  jobList: "/job",
  jobCreate: "/job/novo",
  jobPlanejamentoList: "/jobs/planejamento",
  pessoaPerfilPage: "/perfil",
  permissoesPage: "/admin/permissoes",
  loteCreate: "/lotes/novo",
  producaoFinanceiroCreateAp: "/producao-financeiro/aps/novo",
  producaoFinanceiroAps: "/producao-financeiro/aps",
  producaoFinanceiroConciliacaoList: "/producao-financeiro/conciliacoes",
  producaoFinanceiroApByOrcamento: (orcamento: number) => `/producao-financeiro/aps/${orcamento}`,
  producaoFinanceiroEditApByOrcamento: (orcamento: number) =>
    `/producao-financeiro/aps/${orcamento}/editar`,
  faturamentoFinanceiroDetails: (id: string | number) => `/financeiro/faturamentos/${id}`,
  producaoFinanceiroConciliacaoDetails: (id: string | number) =>
    `/producao-financeiro/conciliacoes/${id}`,
  producaoFinanceiroConciliacaoConciliar: (id: string | number) =>
    `/producao-financeiro/conciliacoes/${id}/conciliar`,
  creditaveisList: "/creditaveis",
  grupoCreditoDetails: (id: string | number) => `/creditaveis/grupo-credito/${id}`,
  contatoFacilitiesDetails: (id: string | number) => `/facilities/contatos/${id}`,
  pedidoBrindeFacilitiesDetails: (id: string | number) =>
    `/facilities/contatos/pedidos-brinde/${id}`,
  tarefaDetails: (id: string | number) => `/tarefas/${id}`,
  tarefaList: "/tarefas",
  tarefaPublicasList: "/tarefas/publicas",
  tarefaCreate: "/tarefas/criar",
  gerenciarClientesList: "/admin/clientes",
  jobTecnologiaDetails: (id: string | number) => `/jobs/${id}/tecnologia`,
  jobTecnologiaList: "/jobs/tecnologia",
  planilhaProducaoGraficaDetails: (planilhaId: string | number) =>
    `/planilhas/${planilhaId}/producao-grafica`,
  planilhaProducaoGraficaList: "/planilhas/producao-grafica",
  criacaoList: "/jobs/criacao",
  criacaoDetails: (id: string | number) => `/jobs/criacao/${id}`,
  usuarioList: "/admin/usuarios",
  usuarioCreate: "/admin/usuarios/novo",
};

// ---------------------------------------------------------
// 3. RESOLVER DE RECURSO (PATTERN DE DICIONÁRIO)
// ---------------------------------------------------------
const RESOURCE_RESOLVERS: Record<RecursoSistema, (params?: any) => string> = {
  [RecursoSistema.PLANILHA_ATENDIMENTO]: (params) => ROUTES.atendimentoDetails(params?.id),
  [RecursoSistema.PLANILHA_COMPRAS]: (params) => ROUTES.compraDetails(params?.id),
  [RecursoSistema.PLANILHA_PRE_PRODUCAO]: (params) => ROUTES.preProducaoDetails(params?.id),
  [RecursoSistema.NOVA_AP]: () => ROUTES.producaoFinanceiroCreateAp,
  [RecursoSistema.AP]: (params) => ROUTES.producaoFinanceiroApByOrcamento(params?.orcamento),
  [RecursoSistema.GERENCIAR_CLIENTES]: () => ROUTES.gerenciarClientesList,
  [RecursoSistema.JOB]: (params) => ROUTES.jobDetails(params?.id),
  [RecursoSistema.FATURAMENTO_ATENDIMENTO]: (params) =>
    ROUTES.faturamentoAtendimentoDetails(params?.id),
  [RecursoSistema.FATURAMENTO_FINANCEIRO]: (params) =>
    ROUTES.faturamentoFinanceiroDetails(params?.id),
  [RecursoSistema.CONTATO_FACILITIES]: (params) => ROUTES.contatoFacilitiesDetails(params?.id),
  [RecursoSistema.PEDIDO_BRINDE_FACILITIES]: (params) =>
    ROUTES.pedidoBrindeFacilitiesDetails(params?.id),
};

export function resolveResourceLink(link?: LinkRecursoDTO): string | undefined {
  if (!link || !link.recurso) {
    return undefined;
  }

  const resolver = RESOURCE_RESOLVERS[link.recurso];

  return resolver ? resolver(link.params) : undefined;
}

// ---------------------------------------------------------
// 4. HOOK DE NAVEGAÇÃO (OTIMIZADO COM USEMEMO)
// ---------------------------------------------------------
export function useNavigationHelpers() {
  const { navigate } = useNavigation();

  const handleNavigate = useCallback(
    (path: string, options?: NavigateOptions) => {
      if (options?.openInNewTab) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else {
        navigate(path, options);
      }
    },
    [navigate],
  );

  return useMemo(() => {
    const go = (path: string, options?: NavigateOptions) => handleNavigate(path, options);

    return {
      goToHome: () => go(ROUTES.home),
      goToLogin: () => go(ROUTES.login),
      goToPurchases: () => go(ROUTES.purchases),
      goToJobCreate: () => go(ROUTES.jobCreate),
      goToUsuarioList: () => go(ROUTES.usuarioList),
      goToUsuarioCreate: () => go(ROUTES.usuarioCreate),
      goToJobDetails: (id: string | number) => go(ROUTES.jobDetails(id)),
      goToJobEdit: (id: string | number) => go(ROUTES.jobEdit(id)),
      goTo: (path: string) => go(path),
      goToAtendimentoDetails: (id: string | number, options?: NavigateOptions) =>
        go(ROUTES.atendimentoDetails(id), options),
      goToPreProducaoDetails: (id: string | number) => go(ROUTES.preProducaoDetails(id)),
      goToCompraDetails: (id: string | number) => go(ROUTES.compraDetails(id)),
      goToPreProducaoCreate: (id: string | number) => go(ROUTES.preProducaoCreate(id)),
      goToVersaoCreate: (id: string | number) => go(ROUTES.versaoCreate(id)),
      goToAtendimentoCreate: (jobId?: string | number) => go(ROUTES.atendimentoCreate(jobId)),
      goToCompraCreate: (id: string | number) => go(ROUTES.compraCreate(id)),
      goToCompraEdit: (id: string | number) => go(ROUTES.compraEdit(id)),
      goToAtendimentoList: () => go(ROUTES.atendimentoList),
      goToAgendaJobs: (options?: NavigateOptions) => go(ROUTES.agendaJobs, options),
      goToPreProducaoList: () => go(ROUTES.preProducaoList),
      goToCompraList: () => go(ROUTES.compraList),
      goToLoteList: () => go(ROUTES.loteList),
      goToAprovacaoList: () => go(ROUTES.aprovacaoList),
      goToJobList: () => go(ROUTES.jobList),
      goToPessoaPerfilPage: () => go(ROUTES.pessoaPerfilPage),
      goToPermissoesPage: () => go(ROUTES.permissoesPage),
      goToLoteCreate: () => go(ROUTES.loteCreate),
      goBack: () => navigate(-1),
      goBackOrFallback: (fallbackPath: () => void) => {
        if (window.history.length > 2) {
          navigate(-1);
        } else {
          fallbackPath();
        }
      },
      goToAtendimentoEdit: (id: string | number) => go(ROUTES.atendimentoEdit(id)),
      goToFaturamentoAtendimentoDetails: (id: string | number) =>
        go(ROUTES.faturamentoAtendimentoDetails(id)),
      goToFaturamentoAtendimentoCreate: (planilhaIds: (string | number)[], tarefaId?: number) =>
        navigate(ROUTES.faturamentoAtendimentoCreate, { state: { planilhaIds, tarefaId } }),
      goToFaturamentoAtendimentoList: () => go(ROUTES.faturamentoAtendimentoList),
      goToFaturamentoFinanceiroList: () => go(ROUTES.financeiroFaturamentoList),
      goToFaturamentoFinanceiroDetails: (id: string | number) =>
        go(ROUTES.faturamentoFinanceiroDetails(id)),
      goToProducaoFinanceiroCreateAp: () => go(ROUTES.producaoFinanceiroCreateAp),
      goToProducaoFinanceiroAps: () => go(ROUTES.producaoFinanceiroAps),
      goToProducaoFinanceiroApByOrcamento: (orcamento: number, options?: NavigateOptions) =>
        go(ROUTES.producaoFinanceiroApByOrcamento(orcamento), options),
      goToEditProducaoFinanceiroApByOrcamento: (orcamento: number) =>
        go(ROUTES.producaoFinanceiroEditApByOrcamento(orcamento)),
      goToConciliacaoList: () => go(ROUTES.producaoFinanceiroConciliacaoList),
      goToConciliacaoDetails: (id: string | number) =>
        go(ROUTES.producaoFinanceiroConciliacaoDetails(id)),
      goToConciliacaoConciliar: (id: string | number) =>
        go(ROUTES.producaoFinanceiroConciliacaoConciliar(id)),
      goToCreditaveisList: () => go(ROUTES.creditaveisList),
      goToGrupoCreditoDetails: (id: string | number) => go(ROUTES.grupoCreditoDetails(id)),
      goToJobPlanejamentoDetails: (id: string | number) => go(ROUTES.jobPlanejamentoDetails(id)),
      goToJobPlanejamentoList: () => go(ROUTES.jobPlanejamentoList),
      goToTarefaDetails: (id: string | number) => go(ROUTES.tarefaDetails(id)),
      goToTarefaList: (tab?: "atribuidas" | "atrasadas" | "criadas" | "observadas") =>
        go(tab ? `${ROUTES.tarefaList}?tab=${tab}` : ROUTES.tarefaList),
      goToTarefaPublicasList: () => go(ROUTES.tarefaPublicasList),
      goToTarefaCreate: () => go(ROUTES.tarefaCreate),
      goToGerenciarClientes: () => go(ROUTES.gerenciarClientesList),
      goToJobTecnologiaDetails: (id: string | number) => go(ROUTES.jobTecnologiaDetails(id)),
      goToJobTecnologiaList: () => go(ROUTES.jobTecnologiaList),
      goToPlanilhaProducaoGraficaDetails: (planilhaId: string | number) =>
        go(ROUTES.planilhaProducaoGraficaDetails(planilhaId)),
      goToPlanilhaProducaoGraficaList: () => go(ROUTES.planilhaProducaoGraficaList),
      goToCriacaoList: () => go(ROUTES.criacaoList),
      goToCriacaoDetails: (id: string | number) => go(ROUTES.criacaoDetails(id)),
    };
  }, [navigate, handleNavigate]);
}
